import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

/*
  Demo videos that auto-cycle on the landing page so visitors can see
  Microlab in motion the moment they land. Each entry pairs a short S3
  clip with the marketing line that describes what the feature does.
  Order matches the funnel — quizzes first (most teachers' #1 pain),
  then teaching materials (presentations), then content management
  (private libraries).
*/
const demoVideos = [
  {
    id: "quizzes",
    title: "Run live quizzes inside your lectures",
    desc: "Create and assign multiple-choice quizzes to your students, watch their answers come in live, and see exactly which slides need more explanation — all without leaving the lecture.",
    src: "https://microlabbucket.s3.eu-west-1.amazonaws.com/slides/public/3989785d-49d1-49da-9373-0ed41c1a76bd/1779664044161-final-quiz-demo.mp4",
  },
  {
    id: "presentations",
    title: "Build presentations from 3D models and zoomable slides",
    desc: "Drop high-resolution histology slides and interactive 3D models straight into a presentation. Zoom in on any region during class without losing image quality.",
    src: "https://microlabbucket.s3.eu-west-1.amazonaws.com/slides/public/3989785d-49d1-49da-9373-0ed41c1a76bd/1779664125980-Presentation.mp4",
  },
  {
    id: "libraries",
    title: "Curate your own private slide libraries",
    desc: "Use Microlab's curated slide collection or upload your own, then pin labels, write annotations, and share the finished library privately with your students.",
    src: "https://microlabbucket.s3.eu-west-1.amazonaws.com/slides/public/3989785d-49d1-49da-9373-0ed41c1a76bd/1779664211902-Private_Libraries___1_.mp4",
  },
];

const problemHooks = [
  "Students forget what they cannot visualize.",
  "Histology lectures are too passive.",
  "Microscopy access is limited, expensive, and inconsistent.",
  "Exam prep is disconnected from real tissue understanding.",
];

const solutionHooks = [
  {
    title: "Professor Control System",
    desc: "Build slide-based quizzes in minutes and monitor engagement from one dashboard.",
  },
  {
    title: "Virtual Lab Teaching",
    desc: "Replace microscope sessions with zoomable digital slides and standardize teaching.",
  },
  {
    title: "AI Teaching Support",
    desc: "Ask AI to explain tissues instantly and generate clearer teaching explanations.",
  },
  {
    title: "Presentation Builder",
    desc: "Turn slides and 3D models into ready-to-teach lessons without starting from scratch.",
  },
];

const featureHooks = [
  {
    title: "Create Exams from Real Slides",
    desc: "Turn histology slides into interactive quizzes instantly.",
  },
  {
    title: "Teach Like a Microscope in Every Student's Pocket",
    desc: "Deliver digital precision without physical lab bottlenecks.",
  },
  {
    title: "AI That Explains Every Tissue",
    desc: "Get instant clarification for difficult microanatomy in class.",
  },
  {
    title: "Build Presentations from Real Medical Data",
    desc: "No manual slide design needed for each lecture.",
  },
];

/*
  Grab a single frame at `atSeconds` from the given video URL and
  return it as a data URL we can use for the visible <video> poster.

  Why client-side instead of pre-generated thumbnails on S3:
    - Zero ops work — adding/replacing demo clips just needs a new
      URL in the `demoVideos` array, no separate poster upload.
    - The first second of marketing-style clips is usually a title
      card or fade-in; the second-second frame is far more useful as
      a still preview.

  Caveats:
    - Requires CORS to be enabled on the bucket so the canvas isn't
      tainted. If it is tainted we silently return `null` and the
      visible <video> just falls back to its native first-frame
      poster — no broken UI.
    - Returns `null` while loading so the card can show a graceful
      "loading" placeholder via its parent.
*/
function useVideoThumbnail(src, atSeconds = 2) {
  const [poster, setPoster] = useState(null);

  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = src;

    const cleanup = () => {
      try {
        video.removeAttribute("src");
        video.load();
      } catch (_) {
        /* ignore */
      }
    };

    const grab = () => {
      if (cancelled) {
        cleanup();
        return;
      }
      try {
        const w = video.videoWidth || 1280;
        const h = video.videoHeight || 720;
        // Cap dimensions so the resulting dataURL stays small (<150 KB
        // for typical 16:9 demo footage) — these are decorative posters,
        // not full-resolution stills.
        const targetW = Math.min(w, 960);
        const targetH = Math.round((h / w) * targetW);
        const canvas = document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, targetW, targetH);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.78);
        if (!cancelled) setPoster(dataUrl);
      } catch (_) {
        /* tainted canvas (CORS) — leave poster null, video falls back */
      } finally {
        cleanup();
      }
    };

    const onLoaded = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const target = duration > 0 ? Math.min(atSeconds, Math.max(duration - 0.1, 0)) : atSeconds;
      try {
        video.currentTime = target;
      } catch (_) {
        cleanup();
      }
    };

    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("seeked", grab, { once: true });
    video.addEventListener("error", cleanup, { once: true });

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("seeked", grab);
      video.removeEventListener("error", cleanup);
      cleanup();
    };
  }, [src, atSeconds]);

  return poster;
}

/*
  Single demo-video card. No autoplay — the visitor presses the native
  play control themselves (or taps the poster on mobile). Three videos
  silently waiting for input is calmer than three loops competing for
  attention and saves bandwidth + battery, especially on cell data.

  The card shows a poster grabbed from the 2-second mark of the clip so
  the still preview always lands on real content instead of a black
  title fade-in. Playback itself still starts from 0.
*/
function DemoVideoCard({ video }) {
  const poster = useVideoThumbnail(video.src, 2);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: "rgba(255,255,255,0.07)",
        border: "1px solid rgba(255,255,255,0.16)",
        backdropFilter: "blur(8px)",
        transition: "transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: "rgba(30,136,229,0.55)",
          boxShadow: "0 18px 40px rgba(0, 30, 90, 0.45)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          bgcolor: "#000",
          overflow: "hidden",
        }}
      >
        <Box
          component="video"
          src={video.src}
          poster={poster || undefined}
          playsInline
          preload="metadata"
          controls
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
          }}
        />
      </Box>
      <Stack spacing={1} sx={{ p: 2.4, flexGrow: 1 }}>
        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.3 }}>
          {video.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.86)", lineHeight: 1.6 }}
        >
          {video.desc}
        </Typography>
      </Stack>
    </Card>
  );
}

export default function LandingPage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 600px at 20% -10%, rgba(33,150,243,0.18), transparent), radial-gradient(1000px 500px at 90% -20%, rgba(156,39,176,0.18), transparent), #0b1220",
        color: "#fff",
        py: { xs: 5, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Stack spacing={2.2} sx={{ height: "100%", justifyContent: "center" }}>
              <Card
                sx={{
                  p: { xs: 2.2, md: 3 },
                  borderRadius: 3,
                  backgroundImage:
                    "linear-gradient(120deg, rgba(7,16,36,0.88), rgba(7,16,36,0.74)), url('/landing-doctor.png')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <Stack spacing={2}>
                  <Chip
                    label="Professor Dashboard"
                    sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff", width: "fit-content" }}
                  />
                  <Typography variant="h2" sx={{ fontWeight: 800, letterSpacing: -0.8, color: "#fff" }}>
                    Stop explaining histology.
                    <br />
                    Start letting students see it.
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#fff", fontWeight: 400 }}>
                    Build quizzes, presentations, and interactive learning directly from real
                    histology tissue - no microscope required.
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#fff" }}>
                    Microlab is a virtual histology teaching system that combines zoomable slides,
                    AI explanation, 3D anatomy, and exam-ready workflows in one platform.
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      component={Link}
                      to="/authentication/sign-in"
                      variant="contained"
                      size="large"
                      sx={{
                        px: 3.5,
                        py: 1.2,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#1e88e5",
                      }}
                    >
                      Login to Dashboard
                    </Button>
                    <Button
                      component={Link}
                      to="/request-demo"
                      variant="outlined"
                      size="large"
                      sx={{
                        px: 3.5,
                        py: 1.2,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontWeight: 700,
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.6)",
                        "&:hover": {
                          borderColor: "#fff",
                          bgcolor: "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      Request a demo
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        <Box sx={{ mb: 4 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "flex-end" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Chip
                label="See it in action"
                size="small"
                sx={{
                  bgcolor: "rgba(30,136,229,0.18)",
                  color: "#bcdcff",
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  mb: 1.2,
                }}
              />
              <Typography
                variant="h4"
                sx={{ color: "#fff", fontWeight: 800, letterSpacing: -0.4 }}
              >
                Microlab in three short demos
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.78)", mt: 0.5, maxWidth: 720 }}
              >
                Short, narrated clips showing what your students and teaching
                assistants will use day to day. Press play on any card to watch.
              </Typography>
            </Box>
          </Stack>
          <Grid container spacing={2.4}>
            {demoVideos.map((video) => (
              <Grid key={video.id} item xs={12} md={4}>
                <DemoVideoCard video={video} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Card
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.16)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 2 }}>
            Why professors switch to Microlab
          </Typography>
          <Grid container spacing={1.2}>
            {problemHooks.map((item) => (
              <Grid key={item} item xs={12} md={6}>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  - {item}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Card>

        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          {solutionHooks.map((feature) => (
            <Grid key={feature.title} item xs={12} sm={6}>
              <Card
                sx={{
                  height: "100%",
                  p: 2.2,
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.86)" }}>
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 1.5 }}>
          Feature highlights
        </Typography>
        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          {featureHooks.map((feature) => (
            <Grid key={feature.title} item xs={12} sm={6}>
              <Card
                sx={{
                  height: "100%",
                  p: 2.2,
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.86)" }}>
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            bgcolor: "rgba(30,136,229,0.16)",
            border: "1px solid rgba(30,136,229,0.4)",
          }}
        >
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800, mb: 1 }}>
            Outcome for your course
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.92)", lineHeight: 1.9 }}>
            Faster lecture preparation - Higher student engagement - Better exam performance -
            Standardized histology teaching - Less dependency on physical lab resources.
          </Typography>
        </Card>
      </Container>
    </Box>
  );
}
